import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '../Modal'
import { getUnidades, getPartes, abrirParte, getColumnasPartes } from '../../api/client'
import { formatFecha } from '../unidades/tipos'
import { hoyLocal } from './fechas'
import PersonaPicker from '../PersonaPicker'
import { idsPersona } from '../unidades/tipos'

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-200'

// "Abrir parte": what a parte can't start without -- date (today by
// default, earlier allowed, never later), unit (one of the Planilla's, never
// an ideal bus), name, and whether it takes the vehicle out of service
// (F/S -- which puts the unit in Mantenimiento until the parte is closed).
// A unit with a parte already open is flagged as soon as it is picked, and
// the server refuses it too. Board columns marked obligatoria (e.g.
// "Ubicación del parte") are asked for here as well.
export default function AbrirParteModal({ onClose, onCreated, onVerParte }) {
  const { t } = useTranslation('engineering')
  const hoy = hoyLocal()
  const [unidades, setUnidades] = useState([])
  const [fecha, setFecha] = useState(hoy)
  const [unidadId, setUnidadId] = useState('')
  const [nombre, setNombre] = useState('')
  // null until answered: it decides the unit's state, so there's no default.
  const [fs, setFs] = useState(null)
  const [abierto, setAbierto] = useState(null)      // the unit's open parte, if any
  const [revisando, setRevisando] = useState(false)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [obligatorias, setObligatorias] = useState([])   // board columns required to open
  const [celdas, setCeldas] = useState({})               // {column name: value}

  useEffect(() => { getUnidades().then(setUnidades).catch(() => setError(t('partes.abrir.unitsFailed'))) }, [t])
  useEffect(() => {
    getColumnasPartes()
      .then(cols => setObligatorias(cols.filter(c => c.obligatoria && c.tipo !== 'archivo')))
      .catch(() => { /* the server still checks them on submit */ })
  }, [])

  // The Planilla's units, grouped by project for the dropdown.
  const porProyecto = useMemo(() => {
    const g = {}
    unidades.forEach(u => { (g[u.proyecto || '—'] ||= []).push(u) })
    Object.values(g).forEach(us => us.sort((a, b) => a.codigo.localeCompare(b.codigo, undefined, { numeric: true })))
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b))
  }, [unidades])
  const unidad = unidades.find(u => String(u.id) === String(unidadId))

  const elegirUnidad = async (id) => {
    setUnidadId(id)
    setAbierto(null)
    setError('')
    if (!id) return
    setRevisando(true)
    try {
      const [p] = await getPartes({ unidad_id: id, estado: 'abierto' })
      setAbierto(p || null)
    } catch { /* the server checks again on submit */ }
    setRevisando(false)
  }

  const submit = async (e) => {
    e.preventDefault()
    if (!fecha) { setError(t('partes.abrir.dateRequired')); return }
    if (fecha > hoy) { setError(t('partes.abrir.noFuture')); return }
    if (!unidadId) { setError(t('partes.abrir.unitRequired')); return }
    if (!nombre.trim()) { setError(t('partes.abrir.nameRequired')); return }
    if (fs === null) { setError(t('partes.abrir.fsRequired')); return }
    const falta = obligatorias.find(c => !String(celdas[c.nombre] ?? '').trim())
    if (falta) { setError(t('partes.abrir.columnRequired', { nombre: falta.nombre })); return }
    if (abierto) return
    setSaving(true)
    setError('')
    try {
      onCreated(await abrirParte({
        nombre: nombre.trim(), unidad_id: Number(unidadId), fecha_apertura: fecha, implica_fs: fs,
        atributos: Object.fromEntries(obligatorias.map(c => [c.nombre, String(celdas[c.nombre]).trim()])),
      }))
      onClose()
    } catch (err) {
      const d = err.response?.data?.detail
      if (err.response?.status === 409 && d?.code === 'parte_abierto') setAbierto({ ...d.parte, unidad_codigo: unidad?.codigo })
      else if (d?.code === 'faltan_obligatorias') setError(t('partes.abrir.columnRequired', { nombre: d.columnas.join(', ') }))
      else setError(typeof d === 'string' ? `${t('partes.abrir.failed')} (${d})` : t('partes.abrir.failed'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal title={t('partes.abrir.title')} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">{t('partes.campos.fecha_apertura')} *</label>
          <input type="date" className={inputCls} value={fecha} max={hoy} onChange={e => setFecha(e.target.value)} />
          <p className="text-[11px] text-gray-400 mt-1">{t('partes.abrir.dateHint')}</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">{t('partes.campos.unidad')} *</label>
          <select className={inputCls} value={unidadId} onChange={e => elegirUnidad(e.target.value)}>
            <option value="">{t('partes.abrir.pickUnit')}</option>
            {porProyecto.map(([p, us]) => (
              <optgroup key={p} label={p}>
                {us.map(u => <option key={u.id} value={u.id}>{u.codigo}</option>)}
              </optgroup>
            ))}
          </select>
          {revisando && <p className="text-[11px] text-gray-400 mt-1">{t('partes.abrir.checking')}</p>}
        </div>
        {abierto && (
          <div role="alert" className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
            <p className="font-semibold">⚠ {t('partes.abrir.alreadyOpenTitle', { codigo: unidad?.codigo || abierto.unidad_codigo })}</p>
            <p className="mt-0.5">
              {t('partes.abrir.alreadyOpen', { numero: abierto.numero, nombre: abierto.nombre, fecha: formatFecha(abierto.fecha_apertura) })}
            </p>
            {onVerParte && (
              <button type="button" onClick={() => { onClose(); onVerParte(abierto.id) }}
                className="mt-1.5 text-xs font-semibold underline hover:no-underline">
                {t('partes.abrir.seeOpen', { numero: abierto.numero })}
              </button>
            )}
          </div>
        )}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">{t('partes.campos.nombre')} *</label>
          <input className={inputCls} value={nombre} onChange={e => setNombre(e.target.value)}
            placeholder={t('partes.abrir.namePlaceholder')} />
        </div>
        <fieldset>
          <legend className="block text-xs font-medium text-gray-700 mb-1">{t('partes.campos.implica_fs')} *</legend>
          <div className="grid grid-cols-2 gap-2">
            {[true, false].map(v => (
              <button key={String(v)} type="button" onClick={() => setFs(v)} aria-pressed={fs === v}
                className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  fs === v
                    ? (v ? 'border-red-400 bg-red-50 text-red-800' : 'border-gray-900 bg-gray-900 text-white')
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                {v ? t('partes.fs.si') : t('partes.fs.no')}
              </button>
            ))}
          </div>
          {fs === true && unidad && (
            <p className="text-[11px] text-red-700 mt-1.5">
              {unidad.estado_actual === 'Baja'
                ? t('partes.fs.hintBaja', { codigo: unidad.codigo })
                : t('partes.fs.hintSi', { codigo: unidad.codigo })}
            </p>
          )}
          {fs === false && <p className="text-[11px] text-gray-400 mt-1.5">{t('partes.fs.hintNo')}</p>}
        </fieldset>
        {obligatorias.map(c => (
          <div key={c.id}>
            <label className="block text-xs font-medium text-gray-700 mb-1">{c.nombre} *</label>
            {c.tipo === 'persona' ? (
              <PersonaPicker multiple value={idsPersona(celdas[c.nombre])}
                onChange={ids => setCeldas(v => ({ ...v, [c.nombre]: ids.length ? JSON.stringify(ids) : '' }))} />
            ) : c.tipo === 'estado' ? (
              <select className={inputCls} value={celdas[c.nombre] || ''}
                onChange={e => setCeldas(v => ({ ...v, [c.nombre]: e.target.value }))}>
                <option value="">{t('partes.abrir.pickOption')}</option>
                {(c.opciones || []).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <input className={inputCls} value={celdas[c.nombre] || ''}
                type={c.tipo === 'fecha' ? 'date' : c.tipo === 'numero' ? 'number' : 'text'} step={c.tipo === 'numero' ? 'any' : undefined}
                onChange={e => setCeldas(v => ({ ...v, [c.nombre]: e.target.value }))} />
            )}
          </div>
        ))}
        {error && <p className="text-red-500 text-xs">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose}
            className="bg-white border border-gray-200 text-gray-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
            {t('unidades.cancel')}
          </button>
          <button type="submit" disabled={saving || revisando || !!abierto}
            className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40 transition-colors">
            {saving ? t('unidades.saving') : t('partes.abrir.submit')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
